import { useEffect, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { EnvVars } from '../../../../../environment';
import { APIBaseChronos } from '@/api/hosts';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import {
  SettingsContent,
  SettingsRow,
  SettingsSaveButton,
  SettingsSaveStatus,
  SettingsSection,
  SettingsSectionFooter,
} from '@/new/components/settings-section';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';

interface RelativityConfig {
  site: string;
  clientId: string;
  clientSecret: string;
}

export function Integrations() {
  const { user } = useUserContext();
  const authInfo = useAuthInfo();
  const { hasPermission } = useHasPermission();
  const navigate = useNavigate();

  // Extract just the subdomain from the full URL
  const extractSubdomain = (url: string) => {
    if (!url) return '';
    const match = url.match(/https?:\/\/([^.]+)\.relativity\.one/);
    return match ? match[1] : url;
  };

  const [config, setConfig] = useState<RelativityConfig>({
    site: extractSubdomain(user?.organisation.relativitySite || ''),
    clientId: user?.organisation.relativityClientId || '',
    clientSecret: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const hasExistingSecret = Boolean(user?.organisation.relativityClientSecretEncrypted);
  const maskedSecret = hasExistingSecret
    ? `${user?.organisation.relativityClientSecretInitialCharacters ?? ''}${'*'.repeat(36)}`
    : 'Enter your Relativity client secret';

  useEffect(() => {
    if (user?.organisation) {
      setConfig((prev) => ({
        ...prev,
        site: extractSubdomain(user.organisation.relativitySite || ''),
        clientId: user.organisation.relativityClientId || '',
      }));
    }
  }, [user?.organisation]);

  const hasUnsavedChanges = user?.organisation
    ? config.site !== extractSubdomain(user.organisation.relativitySite || '') ||
      config.clientId !== (user.organisation.relativityClientId || '') ||
      config.clientSecret !== ''
    : false;

  const getConfigurationStatus = () => {
    if (!user?.organisation) {
      return null;
    }

    const hasSavedConfig =
      hasExistingSecret && user.organisation.relativitySite && user.organisation.relativityClientId;

    if (!hasSavedConfig) {
      return 'not-set-up';
    }

    if (hasUnsavedChanges) {
      return 'not-saved';
    }

    return 'saved';
  };

  const configStatus = getConfigurationStatus();

  const handleInputChange = (field: keyof RelativityConfig, value: string) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveConfig = async () => {
    setIsLoading(true);
    try {
      const token = await authInfo.tokens.getAccessToken();
      const response = await fetch(`${APIBaseChronos}/api/org/relativity-config`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          relativitySite: `https://${config.site}.relativity.one`,
          relativityClientId: config.clientId,
          relativityClientSecret: config.clientSecret || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      toast.success('Relativity configuration saved successfully');

      // Clear the client secret field after successful save
      setConfig((prev) => ({ ...prev, clientSecret: '' }));
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasPermission(Permission.IntegrationsManage)) {
    return (
      <SettingsContent>
        <SettingsSection title="Relativity integration">
          <SettingsRow
            title="Permission required"
            description="You do not have permission to manage integrations. Please contact your administrator or the Wexler team."
          />
        </SettingsSection>
      </SettingsContent>
    );
  }

  return (
    <SettingsContent>
      <SettingsSection
        title="Relativity integration"
        description={
          <>
            Connect to your Relativity instance for document import. See the{' '}
            <Dialog>
              <DialogTrigger className="text-blue-500 duration-150 hover:text-blue-600">
                setup instructions
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    <Icon className="lucide--squares-unite" /> Setup instructions
                  </DialogTitle>
                  <DialogDescription>How to configure the Relativity integration.</DialogDescription>
                </DialogHeader>

                <ol className="list-inside list-decimal space-y-4">
                  <li>
                    Contact your Relativity administrator to create a OAuth2 client with the following configuration:
                    <ol className="mt-3 list-inside list-disc space-y-0.5 pl-8">
                      <li>Flow Grant Type: Code</li>
                      <li>
                        Redirect URL:{' '}
                        <code className="rounded-md border border-gray-300 bg-gray-100 px-1.5 py-0.5 text-xs">
                          {EnvVars.API_URL}/oauth/relativity
                        </code>
                      </li>
                      <li>Access Token Lifetime: 60 minutes</li>
                      <li>Refresh Tokens: Enabled</li>
                    </ol>
                  </li>
                  <li>Enter your Relativity site URL, client ID, and client secret on this page.</li>
                  <li>Save the configuration to enable the Relativity integration.</li>
                </ol>

                <DialogFooter>
                  <DialogClose>
                    <Button>Done</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>{' '}
            to get started.
          </>
        }
        footer={
          <SettingsSectionFooter>
            {configStatus === 'saved' && <SettingsSaveStatus variant="saved">Configuration saved</SettingsSaveStatus>}
            {configStatus === 'not-saved' && (
              <SettingsSaveStatus variant="unsaved">Configuration not saved</SettingsSaveStatus>
            )}
            {configStatus === 'not-set-up' && (
              <SettingsSaveStatus variant="neutral">Configuration not set up</SettingsSaveStatus>
            )}
            {configStatus === null && <span />}

            <SettingsSaveButton
              isLoading={isLoading}
              hasUnsavedChanges={hasUnsavedChanges}
              label="Save Configuration"
              onClick={handleSaveConfig}
              disabled={!config.site || !config.clientId || (!hasExistingSecret && !config.clientSecret)}
            />
          </SettingsSectionFooter>
        }
      >
        <SettingsRow title="Relativity site URL" description="The subdomain of your Relativity instance.">
          <div className="flex w-84 items-center gap-1">
            <span className="text-sm text-gray-500">https://</span>
            <Input
              id="site"
              type="text"
              placeholder="your-site"
              value={config.site}
              onChange={(e) => handleInputChange('site', e.target.value)}
              className="min-w-0 flex-1"
            />
            <span className="text-sm text-gray-500">.relativity.one</span>
          </div>
        </SettingsRow>

        <SettingsRow title="Client ID" description="The OAuth2 client ID for your Relativity instance.">
          <Input
            id="clientId"
            type="text"
            className="w-84"
            placeholder="Enter your Relativity client ID"
            value={config.clientId}
            onChange={(e) => handleInputChange('clientId', e.target.value)}
          />
        </SettingsRow>

        <SettingsRow
          title="Client secret"
          description={
            hasExistingSecret
              ? 'You already have a client secret configured. Enter a new one to update it.'
              : 'The OAuth2 client secret. Stored encrypted.'
          }
        >
          <Input
            id="clientSecret"
            type="password"
            className="w-84"
            placeholder={maskedSecret}
            value={config.clientSecret}
            onChange={(e) => handleInputChange('clientSecret', e.target.value)}
          />
        </SettingsRow>
      </SettingsSection>

      {configStatus === 'saved' && (
        <SettingsSection title="Workspace field mappings">
          <SettingsRow
            title="Field mappings configurator"
            description="Configure the Relativity metadata fields to sync when importing from Relativity."
            className="cursor-pointer transition-colors hover:bg-gray-50"
            onClick={() => navigate('/organisation-settings/integrations/field-mappings')}
          >
            <Icon className="lucide--chevron-right mr-2 text-gray-400" />
          </SettingsRow>
        </SettingsSection>
      )}
    </SettingsContent>
  );
}
